import type { FunctionComponent } from "react";
import React, { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import _ from "lodash";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { AreaLightGraph, shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes, mixins } from "@similarweb/styles";
import { PlainTooltip, IconButton } from "@similarweb/ui-components";

import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import QualificationSideBarContext from "app/components/QualificationSideBar/QualificationSidebarContext";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    minAbbrNumberFilter,
    minVisitsAbbrFilter,
    percentageFilter,
    swNumberFilter,
    timeFilter,
} from "app/filters/ngFilters";
import { getSubTitleFilters } from "app/pages/website-analysis/components/qualification-sidebar/components/helpers";
import {
    ContentWrapper,
    CtaWrapper,
    StyledBox,
    TitleWrapper,
} from "app/pages/website-analysis/components/qualification-sidebar/components/StyledComponents";
import { ComponentsLoader } from "app/pages/website-analysis/components/qualification-sidebar/components/TitleLoader";
import type { ISideBarComponentProps } from "app/pages/website-analysis/components/qualification-sidebar/QualificationSideBarContainer";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { allTrackers } from "app/services/track/track";

const ValueWrapper = styled.span`
    padding-right: 4px;
    color: ${colorsPalettes.midnight[500]};
`;

const GraphWrapper = styled.span`
    height: 100%;
    margin: 0 8px 5px 8px;
`;

const AvgVisitsWrapper = styled(FlexColumn)`
    line-height: 26px;
    padding: 0 24px;
`;

const BoldText = styled.span`
    font-weight: 700;
`;

const TooltipContentWrapper = styled.div`
    display: inline;
`;

const MetricValueWrapper = styled(FlexRow)`
    align-items: center;
`;

const BottomMetricsWrapper = styled(FlexRow)`
    justify-content: space-between;
    padding: 0 24px;
`;

const TextWrapper = styled.span`
    ${mixins.setFont({ $size: 13, $color: colorsPalettes.carbon[500], $weight: 400 })};
`;

const Title = styled.span<{ isBig?: boolean }>`
    ${({ isBig = false }) =>
        mixins.setFont({
            $size: isBig ? 16 : 14,
            $color: colorsPalettes.carbon[250],
            $weight: 400,
        })}
    margin-bottom: 11px;
`;

const Value = styled.span<{ isBig?: boolean }>`
    ${({ isBig = false }) =>
        mixins.setFont({
            $size: isBig ? 32 : 16,
            $color: colorsPalettes.carbon[500],
            $weight: isBig ? 400 : 500,
        })}
`;

const IconWrapper = styled.span`
    margin-right: 12px;
    > div {
        height: 20px;
        width: 20px;
    }
`;

const StyledContentWrapper = styled(FlexColumn)<{ height: string }>`
    justify-content: space-between;
    ${({ height }) => height && `height:${height}`};
`;

interface IItem {
    Key: string;
    Value: string;
}

interface IData {
    bottomMetrics: IMetricInfo[];
    avgMonthlyVisits: IMetricInfo;
    graphBreakDown: any;
}

interface IMetricInfo {
    icon?: IconName;
    title: string;
    value: number;
    tooltip: string;
    filter: any;
}

const metricsInfo = {
    PagesPerVisit: {
        icon: "pages-per-visit" as IconName,
        title: "qualification.sidebar.engagement.metrics.pages.per.visit.title",
        filter: swNumberFilter,
        tooltip: "qualification.sidebar.engagement.metrics.pages.per.visit.title.tooltip",
    },
    BounceRate: {
        icon: "bounce-rate-2" as IconName,
        title: "qualification.sidebar.engagement.metrics.bounce.rate.title",
        filter: percentageFilter,
        tooltip: "qualification.sidebar.engagement.metrics.bounce.rate.title.tooltip",
    },
    UniqueUsers: {
        icon: "monthly-unique-visitors" as IconName,
        title: "qualification.sidebar.engagement.metrics.unique.visits.title",
        filter: minVisitsAbbrFilter,
        tooltip: "qualification.sidebar.engagement.metrics.unique.visits.title.tooltip",
    },
    AvgVisitDuration: {
        icon: "avg-visit-duration" as IconName,
        title: "qualification.sidebar.engagement.metrics.duration.title",
        filter: timeFilter,
        tooltip: "qualification.sidebar.engagement.metrics.duration.title.tooltip",
    },
    AvgMonthVisits: {
        title: "qualification.sidebar.engagement.metrics.avg.visits.title",
        filter: minAbbrNumberFilter,
        tooltip: "qualification.sidebar.engagement.metrics.avg.visits.title.tooltip",
    },
};

const EngagementMetrics: FunctionComponent<ISideBarComponentProps> = ({
    webSource,
    duration,
    country,
    onEmptyData,
    isWWOApiController,
    componentId,
}) => {
    const baseCtrl = "WebsiteOverview";
    const apiControllerForVisits = isWWOApiController ? "WorldwideOverviewVisits" : baseCtrl;
    const apiControllerForOverview = isWWOApiController ? "WorldwideOverview" : baseCtrl;
    const { qualificationSideBarSelection } = React.useContext(QualificationSideBarContext);
    const domain = qualificationSideBarSelection?.data?.domain;
    const i18n = i18nFilter();
    const fetchService = DefaultFetchService.getInstance();
    const durationObject = DurationService.getDurationData(duration, undefined, componentId);
    const { from, to } = durationObject.forAPI;
    const title = i18n("qualification.sidebar.engagement.metrics.title");
    const tooltip = i18n(
        isWWOApiController
            ? "qualification.sidebar.engagement.metrics.title.tooltip.wwoctrl"
            : "qualification.sidebar.engagement.metrics.title.tooltip",
    );
    const subTitleFilters = useMemo(
        () => getSubTitleFilters({ webSource, duration, country }),
        [webSource, duration, country],
    );
    const [data, setData] = useState<IData>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);

    useEffect(() => {
        async function fetchData() {
            setData(null);
            setIsLoading(true);

            const graphDataPromise = fetchService
                .get<{ Data: object[] }>(`widgetApi/${apiControllerForVisits}/EngagementVisits/Graph`, {
                    country,
                    from,
                    includeSubDomains: true,
                    isWindow: false,
                    keys: domain,
                    timeGranularity: "Weekly",
                    to,
                    webSource: "Combined",
                    compareTo: null,
                    compareFrom: null,
                    ShouldGetVerifiedData: false,
                })
                .then((res) => res.Data[domain]);

            const metricsDataPromise = fetchService
                .get<{ Data: object[] }>(`widgetApi/${apiControllerForOverview}/EngagementOverview/Table`, {
                    country,
                    from,
                    includeSubDomains: true,
                    isWindow: false,
                    keys: domain,
                    timeGranularity: "Monthly",
                    to,
                    ShouldGetVerifiedData: false,
                    webSource,
                    compareTo: null,
                    compareFrom: null,
                })
                .then((res: any) => res.Data.find((item) => item.Source === devicesTypes.TOTAL));

            try {
                const [graphData, metricsData] = await Promise.all([graphDataPromise, metricsDataPromise]);
                setData(transformData(graphData, metricsData));
            } catch (e) {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [domain]);

    const transformData = (graphData, metricsData) => {
        let dates = [];
        const graphDataValues: IItem[][][] = Object.values(graphData);
        graphDataValues?.forEach((item: IItem[][]) => {
            const a = item[0].map((i: IItem) => i.Key);
            dates = [...dates, ...a];
        });
        const data = Array.from(new Set(dates))
            .sort((a, b) => (new Date(a.Key) > new Date(b.Key) ? 1 : -1))
            .map((item) => [
                new Date(item).getTime(),
                graphDataValues[0][0].find((value: IItem) => value.Key === item).Value +
                    graphDataValues[1][0].find((value: IItem) => value.Key === item).Value,
            ]);

        return {
            graphBreakDown: [{ data }],
            avgMonthlyVisits: {
                value: metricsData?.AvgMonthVisits,
                ...metricsInfo.AvgMonthVisits,
            },
            bottomMetrics: [
                {
                    value: metricsData?.UniqueUsers,
                    ...metricsInfo.UniqueUsers,
                },

                {
                    value: metricsData?.AvgVisitDuration,
                    ...metricsInfo.AvgVisitDuration,
                },

                {
                    value: metricsData?.PagesPerVisit,
                    ...metricsInfo.PagesPerVisit,
                },

                {
                    value: metricsData?.BounceRate,
                    ...metricsInfo.BounceRate,
                },
            ],
        };
    };

    const getGraphConfig = () => ({
        chart: {
            zooming: {
                type: null,
            },
            marginRight: 10,
            marginLeft: 17,
            marginBottom: 24,
            marginTop: 4,
        },
        yAxis: {
            min: 0,
        },
        plotOptions: {
            series: {
                marker: {
                    states: {
                        hover: {
                            enabled: true,
                            radius: 4,
                        },
                    },
                },
            },
        },
        tooltip: {
            enabled: true,
            outside: true,
            useHTML: true,
            ...tooltipContainerConfig().tooltip,
            formatter() {
                const getTooltipContent = () => {
                    const from = dayjs.utc(this.x).format(shortMonthDayAndYear);
                    const points: any = _.last(this.series.points);
                    const isLast: boolean = points.x === this.x;
                    const dateTo = dayjs.utc(this.x).add(6, "days");
                    const newDateTo =
                        isLast && dateTo.month() !== dayjs.utc(this.x).month()
                            ? dayjs.utc(this.x).endOf("month").startOf("day").utc()
                            : dateTo;
                    const to = newDateTo.format(shortMonthDayAndYear);
                    return (
                        <TooltipContentWrapper>
                            <ValueWrapper>
                                <BoldText>{minVisitsAbbrFilter()(this.y)}</BoldText>
                            </ValueWrapper>
                            <TextWrapper>
                                {i18n("qualification.sidebar.engagement.metrics.graph.tooltip.part1")}
                                <br />
                                {i18n("qualification.sidebar.engagement.metrics.graph.tooltip.part2")}{" "}
                                <BoldText>{from} </BoldText> <br />
                                {i18n("qualification.sidebar.engagement.metrics.graph.tooltip.part3")}{" "}
                                <BoldText>{to}</BoldText>
                            </TextWrapper>
                        </TooltipContentWrapper>
                    );
                };
                return ReactDOMServer.renderToString(getTooltipContent());
            },
        },
    });

    const checkIfNoData = () => {
        const isNoData =
            !data ||
            isError ||
            (!data?.avgMonthlyVisits &&
                data?.bottomMetrics.length === 0 &&
                data?.graphBreakDown[0].data.length === 0) ||
            (!data?.avgMonthlyVisits.value &&
                !data?.bottomMetrics[0].value &&
                data?.graphBreakDown[0].data[0][1] === 0);
        if (isNoData) {
            onEmptyData("qualification_engagement_metrics");
        }
        return isNoData;
    };

    const getTitle = () => (
        <TitleWrapper>
            <WidgetTitle
                title={title}
                tooltip={tooltip}
                filters={subTitleFilters}
            />
        </TitleWrapper>
    );

    const getMetricValue = (metric) => {
        const value = metric?.value;
        if (value === 0) {
            return "N/A";
        }
        const filter = metric?.filter;
        if (metric && filter) {
            switch (filter) {
                case minVisitsAbbrFilter:
                    return metric.filter()(value);
                case timeFilter:
                    return metric.filter()(value);
                case swNumberFilter:
                    return metric.filter()(value, 2);
                case percentageFilter:
                    return value ? metric.filter()(value, 2) + "%" : "N/A";
            }
        }
        return "N/A";
    };

    const onCtaClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "QualificationSidebar/performance/see_more");
    };

    const getContent = () => {
        const ctaHref = proNavigator.href(
            "digitalsuite_website_websiteperformance",
            {
                key: domain,
                country,
                duration,
                isWWW: "*",
                webSource,
            },
            {
                absolute: true,
            },
        );
        return checkIfNoData() ? (
            <ContentWrapper height="250px">
                <NoDataLandscape
                    title="qualification.sidebar.engagement.metrics.no-data.title"
                    subtitle="qualification.sidebar.engagement.metrics.no-data.subtitle"
                />
            </ContentWrapper>
        ) : (
            <>
                <StyledContentWrapper height={"260px"}>
                    <AvgVisitsWrapper>
                        <Title isBig={true}>
                            {i18n(
                                isWWOApiController
                                    ? "qualification.sidebar.engagement.metrics.avg.visits.title.wwoctrl"
                                    : data?.avgMonthlyVisits.title,
                            )}
                        </Title>
                        <Value isBig={true}>
                            {data?.avgMonthlyVisits.value === 0
                                ? "N/A"
                                : data?.avgMonthlyVisits.filter()(data?.avgMonthlyVisits.value)}
                        </Value>
                    </AvgVisitsWrapper>
                    <GraphWrapper>
                        {data?.graphBreakDown ? (
                            <AreaLightGraph
                                data={data.graphBreakDown}
                                graphConfig={getGraphConfig()}
                                height={141}
                                width={null}
                            />
                        ) : null}
                    </GraphWrapper>
                    <BottomMetricsWrapper>
                        {data?.bottomMetrics.map((metric, index) => (
                            <PlainTooltip
                                maxWidth={230}
                                tooltipContent={i18n(metric.tooltip)}
                                key={index}
                            >
                                <FlexColumn>
                                    <Title>{i18n(metric.title)}</Title>
                                    <MetricValueWrapper>
                                        <IconWrapper>
                                            <SWReactIcons iconName={metric.icon} />
                                        </IconWrapper>
                                        <Value>{getMetricValue(metric)}</Value>
                                    </MetricValueWrapper>
                                </FlexColumn>
                            </PlainTooltip>
                        ))}
                    </BottomMetricsWrapper>
                </StyledContentWrapper>
                <CtaWrapper className={"submit-button"}>
                    <PlainTooltip
                        maxWidth={220}
                        tooltipContent={i18n("qualification.sidebar.engagement.metrics.cta.tooltip")}
                    >
                        <a
                            href={ctaHref}
                            target="_blank"
                            rel="noreferrer"
                            onClick={onCtaClick}
                        >
                            <IconButton
                                type="outlined"
                                iconName="dashboard"
                                iconSize="xs"
                            >
                                {i18n("qualification.sidebar.engagement.metrics.cta")}
                            </IconButton>
                        </a>
                    </PlainTooltip>
                </CtaWrapper>
            </>
        );
    };

    return (
        <StyledBox
            maxHeight="460px"
            data-automation="qualification-sidebar-engagement-metrics"
        >
            {isLoading ? (
                <ComponentsLoader />
            ) : (
                <>
                    {getTitle()} {getContent()}
                </>
            )}
        </StyledBox>
    );
};

export const MemoizedEngagementMetrics = React.memo(EngagementMetrics);
